/**
 * OS TRÊS NÍVEIS — setor, tanque, ficha.
 *
 * Pedido do gestor em 21/09/2026: «existem pisciculturas com mais de 100
 * tanques em diferentes setores… a tela inicia com os setores encolhidos,
 * abre-se um setor com os tanques encolhidos, o operador seleciona o tanque e
 * pronto, chega onde quer».
 *
 * Trocar rolagem por toque só vale sob uma condição, e ela manda no desenho
 * inteiro deste arquivo: **a linha fechada tem de responder à pergunta da
 * tela**. Se ela disser só «T-4 ▸», quem procura onde tratar abre os dez
 * tanques para descobrir — e aí a troca piorou. Por isso o resumo é obrigatório
 * em quem usa este componente, e não um enfeite opcional.
 *
 * ── ABRE QUANTOS QUISER ───────────────────────────────────────────────────
 *
 * Os tanques NÃO são sanfona: abrir o T-4 não fecha o T-3. A tela é um painel
 * de comparação, e comparar exige ver os dois. Quem abriu demais encolhe tudo
 * num toque, no botão que aparece a partir do segundo aberto.
 *
 * O SETOR continua um de cada vez, e por outra razão: é ele que responde «onde
 * está o serviço hoje», e essa pergunta se lê melhor com uma lista curta de
 * linhas fechadas do que com a fazenda inteira estendida.
 *
 * ── AS DUAS REGRINHAS, e nenhuma delas é configuração ─────────────────────
 *
 * «Eu, que só tenho um setor, não faço sentido ficar tudo encolhido num setor
 * só.» Certo — então **o nível do setor só existe quando há mais de um**. Com
 * zero ou um, a lista de tanques aparece direto, como sempre apareceu. Nada a
 * ligar, nada a escolher: a tela ganha o nível quando a fazenda ganha o
 * segundo setor, e perde se voltar a ter um.
 *
 * «Como dar preferência a um setor ficar mais evidente?» A resposta que eu
 * recomendo é não ter preferência nenhuma — e fazer a **linha fechada do setor
 * dizer onde está o trabalho**: «Norte · 12 tanques · 4 pendentes». Quem abre a
 * tela vê num relance qual setor tem serviço, e não precisa que ninguém tenha
 * configurado nada.
 *
 * Amarrar setor a pessoa no cadastro seria trabalho de administração que
 * envelhece: gente troca de setor, cobre a folga do outro, e no dia em que
 * isso acontece o app abre o setor errado com confiança. O que o app faz em vez
 * disso é LEMBRAR o último setor aberto NAQUELE APARELHO — o celular do
 * tratador do Norte abre no Norte porque é o que ele usa, sem ninguém declarar.
 * É o mesmo mecanismo que já lembra a unidade e a ração por tanque.
 */

import { useEffect, useMemo, useState } from 'react'

export interface ItemAgrupavel {
  /** O que identifica a linha para o React e para a memória de aberto/fechado. */
  chave: string
  /** O código do tanque, que é o que a pessoa procura. */
  titulo: string
  /** Nulo quando o tanque não tem setor — ele cai em «sem setor». */
  setor: string | null
  /** Verdadeiro quando este item ainda pede alguma coisa de quem está lendo. */
  pendente: boolean
}

const SEM_SETOR = 'sem setor'

/** Lembra por tela, e por aparelho. Nada disto viaja para o servidor. */
function lembrar(tela: string, setor: string | null): void {
  try {
    if (setor === null) localStorage.removeItem(`cunhatai.setor.${tela}`)
    else localStorage.setItem(`cunhatai.setor.${tela}`, setor)
  } catch {
    /* armazenamento bloqueado: a preferência vale só nesta sessão */
  }
}

function lembrado(tela: string): string | null {
  try {
    return localStorage.getItem(`cunhatai.setor.${tela}`)
  } catch {
    return null
  }
}

export function Agrupamento<T extends ItemAgrupavel>({
  tela, itens, resumo, ficha,
}: {
  /** «trato», «ronda» — separa a memória de uma tela da da outra. */
  tela: string
  itens: T[]
  /** A LINHA FECHADA. É ela que decide se encolher ajuda ou atrapalha. */
  resumo: (item: T) => React.ReactNode
  /** A ficha inteira, só quando aberta. */
  ficha: (item: T) => React.ReactNode
}) {
  const setores = useMemo(() => {
    const vistos: string[] = []
    for (const i of itens) {
      const s = i.setor ?? SEM_SETOR
      if (!vistos.includes(s)) vistos.push(s)
    }
    return vistos
  }, [itens])

  // O NÍVEL DO SETOR SÓ EXISTE COM MAIS DE UM. Com um só, ele seria uma gaveta
  // com tudo dentro e nada fora — um toque a mais para chegar ao mesmo lugar.
  const porSetor = setores.length > 1

  const [setorAberto, setSetorAberto] = useState<string | null>(() => {
    if (!porSetor) return null
    const guardado = lembrado(tela)
    return guardado !== null && setores.includes(guardado) ? guardado : null
  })
  // VÁRIOS TANQUES ABERTOS AO MESMO TEMPO — pedido do gestor em 22/09/2026:
  // «de repente seria interessante abrir e manter aberta, para fins de
  // comparação». Ele tem razão, e o motivo é o que a tela é: um painel de
  // comparação entre tanques. Abrir o T-4 para ver o oxigênio e perder o T-3
  // de vista obriga a decorar o número do anterior — que é exatamente o
  // trabalho que a tela deveria fazer por quem está lendo.
  //
  // Era sanfona por inércia, não por decisão. A sanfona serve quando o
  // conteúdo é longo e só um interessa por vez; aqui o conteúdo é curto e a
  // pergunta é entre tanques.
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  // QUEM JÁ FOI ABERTO CONTINUA MONTADO, só escondido.
  //
  // A ficha guarda o que está sendo digitado — o oxigênio na Ronda, a
  // quantidade no compositor do Trato. Desmontando ao fechar, tocar no
  // cabeçalho do tanque, que agora é um botão grande logo acima do
  // formulário, apagava os números sem aviso. Antes das gavetas não havia
  // como perder o que estava digitado, e a gaveta não pode ter custado isso.
  //
  // Só quem foi aberto fica montado: numa fazenda de cem tanques, montar
  // todos de uma vez devolveria o peso que as gavetas existem para tirar.
  const [jaAbertos, setJaAbertos] = useState<Set<string>>(
    // O setor lembrado nasce montado: ele já vem aberto na primeira pintura.
    () => new Set(setorAberto === null ? [] : [`setor:${setorAberto}`]),
  )

  // Setor que deixou de existir — tanque inativado, cadastro corrigido — não
  // pode deixar a tela presa num setor vazio.
  useEffect(() => {
    if (setorAberto !== null && !setores.includes(setorAberto)) setSetorAberto(null)
  }, [setores, setorAberto])

  function abrirSetor(s: string) {
    const novo = setorAberto === s ? null : s
    setSetorAberto(novo)
    if (novo !== null) setJaAbertos((x) => new Set(x).add(`setor:${novo}`))
    lembrar(tela, novo)
  }

  function alternar(chave: string) {
    setAbertos((x) => {
      const novo = new Set(x)
      if (novo.has(chave)) novo.delete(chave)
      else novo.add(chave)
      return novo
    })
    setJaAbertos((x) => new Set(x).add(chave))
  }

  function linhaDoTanque(item: T) {
    const aberto = abertos.has(item.chave)
    return (
      <div key={item.chave} className={aberto ? 'gaveta aberta' : 'gaveta'}>
        <button
          type="button"
          className="gaveta-linha"
          onClick={() => alternar(item.chave)}
          aria-expanded={aberto}
        >
          <span className="gaveta-seta">{aberto ? '▾' : '▸'}</span>
          <span className="gaveta-titulo">{item.titulo}</span>
          <span className="gaveta-resumo">{resumo(item)}</span>
        </button>
        {jaAbertos.has(item.chave) && (
          <div className="gaveta-corpo" hidden={!aberto}>
            {ficha(item)}
          </div>
        )}
      </div>
    )
  }

  // Só conta o que está de fato à vista: tanque aberto dentro de setor
  // fechado não incomoda ninguém e não deveria pesar no contador.
  const visiveis = itens.filter(
    (i) => abertos.has(i.chave) && (!porSetor || (i.setor ?? SEM_SETOR) === setorAberto),
  )

  const encolherTudo =
    visiveis.length > 1 ? (
      <div className="encolher-tudo">
        <button
          type="button"
          className="discreto"
          onClick={() => setAbertos(new Set())}
        >
          encolher os {visiveis.length} abertos
        </button>
      </div>
    ) : null

  if (!porSetor) {
    return (
      <>
        {encolherTudo}
        {itens.map(linhaDoTanque)}
      </>
    )
  }

  return (
    <>
      {encolherTudo}
      {setores.map((s) => {
        const doSetor = itens.filter((i) => (i.setor ?? SEM_SETOR) === s)
        const pendentes = doSetor.filter((i) => i.pendente).length
        const aberto = setorAberto === s
        return (
          <div key={s} className={aberto ? 'setor aberto' : 'setor'}>
            <button
              type="button"
              className="setor-linha"
              onClick={() => abrirSetor(s)}
              aria-expanded={aberto}
            >
              <span className="gaveta-seta">{aberto ? '▾' : '▸'}</span>
              <span className="setor-nome">{s}</span>
              {/* ONDE ESTÁ O TRABALHO, na linha fechada. É isto que dispensa
                  qualquer configuração de «setor preferido». */}
              <span className="setor-conta">
                {doSetor.length} {doSetor.length === 1 ? 'tanque' : 'tanques'}
                {pendentes > 0 && <strong> · {pendentes} pendente{pendentes > 1 ? 's' : ''}</strong>}
              </span>
            </button>
            {/* Pelo mesmo motivo do tanque: fechar o setor não pode desmontar
                o que está dentro dele e levar junto o que foi digitado. */}
            {jaAbertos.has(`setor:${s}`) && (
              <div className="setor-corpo" hidden={!aberto}>
                {doSetor.map(linhaDoTanque)}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

/**
 * O botão que volta ao topo.
 *
 * Aparece só depois de rolar, porque botão parado em cima de uma tela que não
 * rolou é enfeite que come área de toque. Respeita a borda de baixo do
 * aparelho — o mesmo `env(safe-area-inset-bottom)` da moldura do app.
 */
export function VoltarAoTopo() {
  const [visivel, setVisivel] = useState(false)

  useEffect(() => {
    const olhar = () => setVisivel(window.scrollY > 600)
    olhar()
    window.addEventListener('scroll', olhar, { passive: true })
    return () => window.removeEventListener('scroll', olhar)
  }, [])

  if (!visivel) return null

  return (
    <button
      type="button"
      className="ao-topo"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="voltar ao topo"
    >
      ▲
    </button>
  )
}
